import React, { useState } from "react";
import { DateTime } from "luxon";

function CalenderSelector({
  availableDates = [],
  setSelectedDate,
  selectedDate,
  register,
  setValue,
  name,
  errors,
  onMonthChange,
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  // Get number of days in the month
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(currentYear, currentMonth, 1).getDay();

  // Create date list
  const dates = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Convert availableDates to YYYY-MM-DD format for easy comparison
  // Use local date to prevent timezone issues
  const availableDatesSet = new Set(
    availableDates
      .map((d) => {
        const date = DateTime.fromISO(d.date, { zone: "local" });
        if (!date.isValid) return null;
        return date.toFormat("yyyy-MM-dd");
      })
      .filter(Boolean) // Remove nulls
  );

  // Handle month change
  const changeMonth = (offset) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + offset);
    setCurrentDate(newDate);
    if (onMonthChange) {
      onMonthChange(newDate.getMonth(), newDate.getFullYear());
    }
  };

  return (
    <div className="border p-2 rounded-md">
      <div className="flex justify-between items-center mb-2">
        <button
          type="button"
          onClick={() => changeMonth(-1)}
          className="text-custom-blue font-bold disabled:opacity-30"
          disabled={
            currentDate.getMonth() === new Date().getMonth() &&
            currentDate.getFullYear() === new Date().getFullYear()
          }
        >
          {"<"}
        </button>
        <p className="text-center font-medium text-xs">
          {currentDate.toLocaleString("default", { month: "long" })}{" "}
          {currentYear}
        </p>
        <button
          type="button"
          onClick={() => changeMonth(1)}
          className="text-custom-blue font-bold"
        >
          {">"}
        </button>
      </div>

      <div className="grid grid-cols-7 text-center gap-1 mt-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
          <span key={i} className="text-custom-blue font-semibold text-xs">
            {d}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 text-center gap-1 mt-1">
        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
          <div key={`empty-${i}`} className="p-1 w-7 h-7"></div>
        ))}

        {dates.map((date) => {
          const dateObj = new Date(currentYear, currentMonth, date);

          // Use local date string to prevent timezone issues
          const dateStr = DateTime.local(
            currentYear,
            currentMonth + 1,
            date
          ).toFormat("yyyy-MM-dd");

          const isAvailable = availableDatesSet.has(dateStr);
          const isSelected =
            selectedDate instanceof Date &&
            selectedDate.getDate() === date &&
            selectedDate.getMonth() === currentMonth &&
            selectedDate.getFullYear() === currentYear;

          return (
            <button
              type="button"
              key={date}
              className={`flex items-center justify-center text-xs font-bold ${
                isAvailable
                  ? "text-black hover:bg-gray-200"
                  : "opacity-50 text-gray-400 cursor-not-allowed"
              }`}
              onClick={() => {
                if (isAvailable) {
                  setSelectedDate(dateObj);
                  setValue(name, dateObj.toISOString());
                }
              }}
              disabled={!isAvailable}
            >
              <div
                className={`rounded-full p-1 w-7 h-7 ${
                  isSelected
                    ? "border-custom-blue text-custom-blue border-2"
                    : ""
                }`}
              >
                {date}
              </div>
            </button>
          );
        })}
      </div>

      <input
        type="hidden"
        {...register(name, { required: "Please select a date." })}
        value={selectedDate ? selectedDate.toISOString() : ""}
      />

      {errors[name] && (
        <p className="text-red-500 text-xs mt-1">{errors[name].message}</p>
      )}
    </div>
  );
}

export default CalenderSelector;
